import type { LeadFlowQuestion, LeadFlowStepEntryInput } from "./types";

import {
  isLeadFormDeliveryContext,
  isLeadFormType,
  LEAD_FORM_CONTRACTS,
} from "./form-contracts";
import { getLeadFlowQuestionFieldNames } from "./get-lead-flow-question-field-names";
import { getRequiredLeadFormFieldNames } from "./get-required-lead-form-field-names";

function hasDefaultAnswer(question: LeadFlowQuestion): boolean {
  return Boolean(
    question.defaultAnswer ||
      question.defaultAnswers?.some((answer) => Boolean(answer)),
  );
}

/**
 * Lists the contract-required fields, asked on steps before `stepIndex`, that
 * a visitor entering the flow at that step would arrive without. A field is
 * satisfied when its question defines a default answer, or when it is passed
 * in `prefilledFieldNames` and the contract allows prefilling it from the
 * query string. Callers pass only fields whose query value already passed the
 * contract validators, so an invalid value counts as missing.
 *
 * An unknown form type or delivery context makes every step after the first
 * unenterable, reported as a `formType` / `deliveryContext` gap.
 */
function getLeadFlowStepEntryMissingFieldNames({
  deliveryContext,
  formType,
  prefilledFieldNames = [],
  stepIndex,
  steps,
}: LeadFlowStepEntryInput): string[] {
  if (stepIndex <= 0) {
    return [];
  }

  if (!isLeadFormType(formType)) {
    return ["formType"];
  }

  if (!isLeadFormDeliveryContext(deliveryContext)) {
    return ["deliveryContext"];
  }

  const requiredFieldNames = new Set(
    getRequiredLeadFormFieldNames({ deliveryContext, formType }),
  );
  const prefillableFieldNames = new Set(
    Object.keys(LEAD_FORM_CONTRACTS[formType].queryPrefillParameters),
  );
  const prefilled = new Set(
    [...prefilledFieldNames].filter((fieldName) =>
      prefillableFieldNames.has(fieldName),
    ),
  );
  const missingFieldNames = new Set<string>();

  for (const step of steps.slice(0, stepIndex)) {
    for (const question of step?.items ?? []) {
      if (!question || hasDefaultAnswer(question)) {
        continue;
      }

      for (const fieldName of getLeadFlowQuestionFieldNames(question)) {
        if (requiredFieldNames.has(fieldName) && !prefilled.has(fieldName)) {
          missingFieldNames.add(fieldName);
        }
      }
    }
  }

  return [...missingFieldNames];
}

export { getLeadFlowStepEntryMissingFieldNames };
